'use client';

import {
  createContext,
  ReactNode,
  useContext,
  useReducer,
  useCallback,
} from 'react';

const DashboardActionTypes: { [key: string]: string } = {
  SET_COMPANY_QUERY_PARAMS: 'SET_COMPANY_QUERY_PARAMS',
  SET_USER_QUERY_PARAMS: 'SET_USER_QUERY_PARAMS',
  SET_SHOW_ADD_EDIT_USER_MODAL: 'SET_SHOW_ADD_EDIT_USER_MODAL',
  SET_SHOW_ADD_EDIT_COMPANY_MODAL: 'SET_SHOW_ADD_EDIT_COMPANY_MODAL',
  SET_SHOW_CONFIRM_DELETE_MODAL: 'SET_SHOW_CONFIRM_DELETE_MODAL',
  SET_SHOW_ADD_DEV_ENVIRONMENT_MODAL: 'SET_SHOW_ADD_DEV_ENVIRONMENT_MODAL',
  SET_SHOW_DEACTIVATE_COMPANY_MODAL: 'SET_SHOW_DEACTIVATE_COMPANY_MODAL',
};

type QueryParams = {
  q: string;
  page: number;
  pageSize: number;
  sort: string;
  order: string;
  sort_search?: string;
  sort_value?: string;
  active_only?: number;
  managers?: number;
  include_support_user?: number;
};

type DashboardState = {
  companyQueryParams: QueryParams;
  userQueryParams?: QueryParams;
  showAddEditUserModal?: boolean;
  showAddEditCompanyModal?: boolean;
  showAddDemoEnvModal?: boolean;
  showConfirmDeleteModal?: boolean;
  showDeactivateCompanyModal?: boolean;
};

const companyState = {
  q: '',
  page: 1,
  pageSize: 50,
  sort: 'name',
  order: 'asc',
  sort_search: undefined,
  sort_value: undefined,
};

const userState = {
  q: '',
  page: 1,
  pageSize: 50,
  sort: 'first_name',
  order: 'asc',
  sort_search: undefined,
  sort_value: undefined,
  active_only: 1,
  include_support_user: 0,
};

const initialState: DashboardState = {
  companyQueryParams: companyState,
  userQueryParams: userState,
  showAddEditUserModal: false,
  showAddEditCompanyModal: false,
  showAddDemoEnvModal: false,
  showConfirmDeleteModal: false,
  showDeactivateCompanyModal: false,
};

const DashboardContext = createContext<{
  state: DashboardState;
  dispatch: React.Dispatch<any>;
}>({
  state: initialState,
  dispatch: () => null,
});

const dashboardReducer = (state: DashboardState, action: any) => {
  switch (action.type) {
    case DashboardActionTypes.SET_COMPANY_QUERY_PARAMS: {
      return {
        ...state,
        companyQueryParams: action.payload,
      };
    }
    case DashboardActionTypes.SET_USER_QUERY_PARAMS: {
      return {
        ...state,
        userQueryParams: action.payload,
      };
    }
    case DashboardActionTypes.SET_SHOW_ADD_EDIT_USER_MODAL: {
      return {
        ...state,
        showAddEditUserModal: action.payload,
      };
    }
    case DashboardActionTypes.SET_SHOW_ADD_EDIT_COMPANY_MODAL: {
      return {
        ...state,
        showAddEditCompanyModal: action.payload,
      };
    }
    case DashboardActionTypes.SET_SHOW_CONFIRM_DELETE_MODAL: {
      return {
        ...state,
        showConfirmDeleteModal: action.payload,
      };
    }
    case DashboardActionTypes.SET_SHOW_DEACTIVATE_COMPANY_MODAL: {
      return {
        ...state,
        showDeactivateCompanyModal: action.payload,
      };
    }
    case DashboardActionTypes.SET_SHOW_ADD_DEV_ENVIRONMENT_MODAL: {
      return {
        ...state,
        showAddDemoEnvModal: action.payload,
      };
    }

    default:
      return state;
  }
};

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);

  return (
    <DashboardContext.Provider value={{ state, dispatch }}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }

  const {
    state: {
      companyQueryParams,
      userQueryParams,
      showAddEditUserModal,
      showAddEditCompanyModal,
      showAddDemoEnvModal,
      showConfirmDeleteModal,
      showDeactivateCompanyModal,
    },
    dispatch,
  } = context;

  const setCompanyQueryParams = useCallback(
    (queryParams: QueryParams) => {
      dispatch({
        type: DashboardActionTypes.SET_COMPANY_QUERY_PARAMS,
        payload: queryParams,
      });
    },
    [dispatch]
  );

  const setUserQueryParams = useCallback(
    (queryParams: QueryParams) => {
      dispatch({
        type: DashboardActionTypes.SET_USER_QUERY_PARAMS,
        payload: queryParams,
      });
    },
    [dispatch]
  );

  const setShowAddEditUserModal = useCallback(
    (showAddEditUserModal: DashboardState['showAddEditUserModal']) => {
      dispatch({
        type: DashboardActionTypes.SET_SHOW_ADD_EDIT_USER_MODAL,
        payload: showAddEditUserModal,
      });
    },
    [dispatch]
  );

  const setShowAddEditCompanyModal = useCallback(
    (showAddEditCompanyModal: DashboardState['showAddEditCompanyModal']) => {
      dispatch({
        type: DashboardActionTypes.SET_SHOW_ADD_EDIT_COMPANY_MODAL,
        payload: showAddEditCompanyModal,
      });
    },
    [dispatch]
  );

  const setShowConfirmDeleteModal = useCallback(
    (showConfirmDeleteModal: DashboardState['showConfirmDeleteModal']) => {
      dispatch({
        type: DashboardActionTypes.SET_SHOW_CONFIRM_DELETE_MODAL,
        payload: showConfirmDeleteModal,
      });
    },
    [dispatch]
  );

  const setShowDeactivateCompanyModal = useCallback(
    (
      showDeactivateCompanyModal: DashboardState['showDeactivateCompanyModal']
    ) => {
      dispatch({
        type: DashboardActionTypes.SET_SHOW_DEACTIVATE_COMPANY_MODAL,
        payload: showDeactivateCompanyModal,
      });
    },
    [dispatch]
  );

  const setshowAddDemoEnvModal = useCallback(
    (showAddDemoEnvModal: DashboardState['showAddDemoEnvModal']) => {
      dispatch({
        type: DashboardActionTypes.SET_SHOW_ADD_DEV_ENVIRONMENT_MODAL,
        payload: showAddDemoEnvModal,
      });
    },
    [dispatch]
  );

  return {
    companyQueryParams,
    setCompanyQueryParams,
    userQueryParams,
    setUserQueryParams,
    showAddEditUserModal,
    setShowAddEditUserModal,
    showConfirmDeleteModal,
    setShowConfirmDeleteModal,
    showAddEditCompanyModal,
    setShowAddEditCompanyModal,
    showAddDemoEnvModal,
    setshowAddDemoEnvModal,
    showDeactivateCompanyModal,
    setShowDeactivateCompanyModal,
  };
};
